import React, { useState, useEffect, useRef, useContext } from "react";
import { Row, Col, Button, Modal, Form } from "react-bootstrap";
import { FaEdit, FaTrash, FaPaperPlane } from "react-icons/fa";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../../../assets/css/customer/dashboard/HelpandSupport.css';
import CustomerLayout from '../../../../layouts/customer/CustomerLayout';
import AppContext from '../../../../context/AppContext';
import { checkLoggedInCustomer } from '../../../../helper/Helper';

const HelpandSupport = () => {

  const navigate = useNavigate();

  const [showModal,        setShowModal]        = useState(false);
  const [subject,          setSubject]          = useState('');
  const [category,         setCategory]         = useState('');
  const [description,      setDescription]      = useState('');

  // ===================== TICKET FORM VALIDATION ERRORS (regexChecker based) =====================
  const [ticketErrors, setTicketErrors] = useState({
    subject: '',
    category: '',
    description: ''
  });

  const [bookings,         setBookings]         = useState([]);
  const [selectedBookingId,setSelectedBookingId] = useState('');
  const [eventName,        setEventName]        = useState('');
  const [bookingDate,      setBookingDate]      = useState('');
  const [customerName,     setCustomerName]     = useState('');

  const [tickets,          setTickets]          = useState([]);
  const [pageLoading,      setPageLoading]      = useState(true);

  const [showEditModal,    setShowEditModal]    = useState(false);
  const [editTicketId,     setEditTicketId]     = useState('');
  const [editSubject,      setEditSubject]      = useState('');
  const [editCategory,     setEditCategory]     = useState('');
  const [editDescription,  setEditDescription]  = useState('');

  // ===================== EDIT TICKET VALIDATION ERRORS =====================
  const [editTicketErrors, setEditTicketErrors] = useState({
    subject: '',
    category: '',
    description: ''
  });

  const [selectedTicket,   setSelectedTicket]   = useState(null);
  const [chatMessages,     setChatMessages]     = useState([]);
  const [newMessage,       setNewMessage]       = useState('');
  const [chatLoading,      setChatLoading]      = useState(false);

  const chatEndRef = useRef(null);
  const { baseUrl, regexChecker } = useContext(AppContext);

  const fetchMyTickets = () => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);
    axios.post(`${baseUrl}/customer/helpandsupport/viewMyTickets`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) setTickets(res.data.tickets);
      setPageLoading(false);
    })
    .catch((err) => { console.log(err); setPageLoading(false); });
  };

  useEffect(() => {
    checkLoggedInCustomer(navigate);

    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
    if (!loggedInCustomer) return;

    fetchMyTickets();
  }, []);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchBookings = () => {
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);
    axios.post(`${baseUrl}/customer/bookings/viewMyBookings`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => { if (res.data.success === true) setBookings(res.data.bookings); })
    .catch((err) => console.log(err));
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setCategory(value);
    setTicketErrors({ ...ticketErrors, category: value ? '' : 'Category is required' });
    if (value !== 'Technical' && value !== '') {
      fetchBookings();
    } else {
      setSelectedBookingId(''); setEventName(''); setBookingDate(''); setCustomerName('');
    }
  };

  const handleBookingSelect = (e) => {
    const selectedId = e.target.value;
    setSelectedBookingId(selectedId);
    const selected = bookings.find((b) => b.id == selectedId);
    if (selected) {
      const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
      setEventName(selected.weevent ? selected.weevent.eventName : '');
      setBookingDate(selected.date);
      setCustomerName(`${loggedInCustomer.firstName} ${loggedInCustomer.lastName}`);
    }
  };

  // ===================== VALIDATION (same style as CustomerProfile / MyEvents) =====================
  const resetTicketErrors = () => {
    setTicketErrors({ subject: '', category: '', description: '' });
  };

  const validateTicketForm = () => {
    const errors = {
      subject: regexChecker('subject', subject),
      category: category ? '' : 'Category is required',
      description: regexChecker('description', description)
    };

    setTicketErrors(errors);

    return Object.values(errors).some((err) => err);
  };

  const handleSubmit = () => {
    const hasErrors = validateTicketForm();
    if (hasErrors) {
      return;
    }

    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));
    const formData = new FormData();
    formData.append('wecustomer_id', loggedInCustomer.id);
    formData.append('eventName',     eventName);
    formData.append('webookingId',   selectedBookingId);
    formData.append('date',          bookingDate);
    formData.append('customerName',  customerName);
    formData.append('subject',       subject);
    formData.append('category',      category);
    formData.append('description',   description);
    axios.post(`${baseUrl}/customer/helpandsupport/store`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) {
        setShowModal(false);
        setSubject(''); setCategory(''); setDescription('');
        setSelectedBookingId(''); setEventName(''); setBookingDate(''); setCustomerName('');
        resetTicketErrors();
        fetchMyTickets();
      }
    })
    .catch((err) => console.log(err));
  };

  const handleEditClick = (ticket) => {
    setEditTicketId(ticket.id);
    setEditSubject(ticket.subject);
    setEditCategory(ticket.category);
    setEditDescription(ticket.description);
    setEditTicketErrors({ subject: '', category: '', description: '' });
    setShowEditModal(true);
  };

  const validateEditTicketForm = () => {
    const errors = {
      subject: regexChecker('subject', editSubject),
      category: editCategory ? '' : 'Category is required',
      description: regexChecker('description', editDescription)
    };

    setEditTicketErrors(errors);

    return Object.values(errors).some((err) => err);
  };

  const handleUpdate = () => {
    const hasErrors = validateEditTicketForm();
    if (hasErrors) {
      return;
    }

    const formData = new FormData();
    formData.append('id',          editTicketId);
    formData.append('subject',     editSubject);
    formData.append('category',    editCategory);
    formData.append('description', editDescription);
    axios.post(`${baseUrl}/customer/helpandsupport/updateTicket`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) { setShowEditModal(false); fetchMyTickets(); }
    })
    .catch((err) => console.log(err));
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      axios.delete(`${baseUrl}/customer/helpandsupport/deleteTicket/${id}`)
      .then((res) => { if (res.data.success === true) fetchMyTickets(); })
      .catch((err) => console.log(err));
    }
  };

  // ── CHAT: View Messages ─────────────────────────
  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setChatLoading(true);
    const formData = new FormData();
    formData.append('ticket_id', ticket.id);
    axios.post(`${baseUrl}/customer/helpandsupport/viewMessages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) {
        setChatMessages(res.data.messages.map((m, i) => ({ ...m, id: i + 1 })));
      }
      setChatLoading(false);
    })
    .catch((err) => { console.log(err); setChatLoading(false); });
  };

  // ── CHAT: Send Message ──────────────────────────
  const handleSendMessage = () => {
    if (newMessage.trim() === '') return;
    const formData = new FormData();
    formData.append('ticket_id', selectedTicket.id);
    formData.append('message',   newMessage.trim());
    axios.post(`${baseUrl}/customer/helpandsupport/sendMessage`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((res) => {
      if (res.data.success === true) {
        const msg = {
          id: chatMessages.length + 1,
          sender: 'customer',
          text: newMessage.trim(),
          time: new Date().toISOString().slice(0, 10),
        };
        setChatMessages((prev) => [...prev, msg]);
        setNewMessage('');
      }
    })
    .catch((err) => console.log(err));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  const getStatusBadgeClass = (status) => {
    if (status === 'Open')    return 'hs-badge-open';
    if (status === 'Pending') return 'hs-badge-pending';
    if (status === 'Closed')  return 'hs-badge-closed';
    return 'hs-badge-resolved';
  };

  return (
    <CustomerLayout>
      <div className="hs-page">

        <div className="hs-header-card">
          <div className="hs-header-left">
            <span className="hs-header-icon">?</span>
            <div>
              <h2 className="hs-header-title">Help &amp; Support</h2>
              <p className="hs-header-subtitle">We're here to help, anytime.</p>
            </div>
          </div>
          <Button className="hs-btn-new-ticket" onClick={() => { resetTicketErrors(); setShowModal(true); }}>+ New Ticket</Button>
        </div>

        <div className="hs-content-card mb-3">
          <Row className="g-3">
            <Col md={6}><div className="hs-faq-card"><h6 className="hs-faq-q">How do I change my event date?</h6><p className="hs-faq-a">You can request a date change from your bookings page.</p></div></Col>
            <Col md={6}><div className="hs-faq-card"><h6 className="hs-faq-q">What payment methods are accepted?</h6><p className="hs-faq-a">Bank transfer, debit/credit cards and easypaisa are supported.</p></div></Col>
            <Col md={6}><div className="hs-faq-card"><h6 className="hs-faq-q">Can I cancel a booking?</h6><p className="hs-faq-a">Yes — refund policy depends on how close to the event date you cancel.</p></div></Col>
            <Col md={6}><div className="hs-faq-card"><h6 className="hs-faq-q">How does the AI planner work?</h6><p className="hs-faq-a">Tell it your budget and preferences and it builds a tailored plan instantly.</p></div></Col>
          </Row>
        </div>

        <div className="hs-content-card">
          <div className="hs-tickets-chat-wrapper">

            <div className={`hs-tickets-panel ${selectedTicket ? 'hs-tickets-panel-shrink' : ''}`}>
              <h6 className="hs-tickets-title">My Tickets</h6>
              {pageLoading ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}><span className="spinner-border spinner-border-sm me-2"/>Loading...</div>
              ) : tickets.length === 0 ? (
                <div style={{ textAlign:"center", padding:"20px 0" }}>No tickets found</div>
              ) : (
                tickets.map((ticket) => (
                  <div className={`hs-ticket-row ${selectedTicket && selectedTicket.id === ticket.id ? 'hs-ticket-row-active' : ''}`} key={ticket.id}>
                    <span className="hs-ticket-id">T-{ticket.id}</span>
                    <span className="hs-ticket-subject">{ticket.subject}</span>
                    <span className="hs-ticket-date">{ticket.created_at ? ticket.created_at.slice(0,10) : ''}</span>
                    <span className={`hs-badge ${getStatusBadgeClass(ticket.status)}`}>{ticket.status}</span>
                    <span className="hs-ticket-actions">
                      <button className="hs-action-btn hs-action-btn-view" onClick={() => handleViewTicket(ticket)}>View →</button>
                      <button className="hs-action-btn" onClick={() => handleEditClick(ticket)}><FaEdit /></button>
                      <button className="hs-action-btn hs-action-btn-delete" onClick={() => handleDelete(ticket.id)}><FaTrash /></button>
                    </span>
                  </div>
                ))
              )}
            </div>

            {selectedTicket && (
              <div className="hs-chat-panel">
                <div className="hs-chat-header">
                  <div className="hs-chat-header-info">
                    <span className="hs-chat-ticket-id">Ticket ID: {selectedTicket.id}</span>
                    <span className="hs-chat-ticket-subject">{selectedTicket.subject}</span>
                  </div>
                  <div className="hs-chat-header-right">
                    <span className={`hs-badge ${getStatusBadgeClass(selectedTicket.status)}`}>Status: {selectedTicket.status}</span>
                    <button className="hs-chat-close-btn" onClick={() => setSelectedTicket(null)}>✕</button>
                  </div>
                </div>

                <div className="hs-chat-messages">
                  {chatLoading ? (
                    <div style={{ textAlign:'center', padding:'20px 0' }}><span className="spinner-border spinner-border-sm me-2"/>Loading...</div>
                  ) : chatMessages.length === 0 ? (
                    <div style={{ textAlign:'center', color:'#9ba8b5', padding:'20px 0', fontSize:'13px' }}>No messages yet</div>
                  ) : (
                    chatMessages.map((msg) => (
                      <div key={msg.id} className={`hs-chat-message-wrapper ${msg.sender === 'customer' ? 'hs-msg-right' : 'hs-msg-left'}`}>
                        <span className="hs-chat-sender-label">{msg.sender === 'customer' ? 'You' : 'Support Team'}</span>
                        <div className={`hs-chat-bubble ${msg.sender === 'customer' ? 'hs-bubble-customer' : 'hs-bubble-support'}`}>{msg.text}</div>
                        <span className="hs-chat-time">{msg.time}</span>
                      </div>
                    ))
                  )}
                  <div ref={chatEndRef} />
                </div>

                <div className="hs-chat-input-bar">
                  <input type="text" className="hs-chat-input" placeholder="Type your message..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} onKeyDown={handleKeyDown} />
                  <button className="hs-chat-send-btn" onClick={handleSendMessage}><FaPaperPlane /></button>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>

      {/* Create Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="hs-modal">
        <Modal.Body className="hs-modal-body">
          <button className="hs-modal-close" onClick={() => setShowModal(false)}>&#x2715;</button>
          <h5 className="hs-modal-title">Create Support Ticket</h5>
          <p className="hs-modal-subtitle">Describe your issue and we'll get back to you shortly.</p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="hs-label">Subject</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Refund query"
                className={`hs-input ${ticketErrors.subject ? 'input-error' : ''}`}
                value={subject}
                onChange={(e) => {
                  setSubject(e.target.value);
                  setTicketErrors({ ...ticketErrors, subject: regexChecker('subject', e.target.value) });
                }}
              />
              {ticketErrors.subject && (
                <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.subject}</p>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="hs-label">Category</Form.Label>
              <Form.Select
                className={`hs-input ${ticketErrors.category ? 'input-error' : ''}`}
                value={category}
                onChange={handleCategoryChange}
              >
                <option value="">Select</option>
                <option value="Booking">Booking</option>
                <option value="Payment">Payment</option>
                <option value="Service">Service</option>
                <option value="Technical">Technical</option>
                <option value="Other">Other</option>
              </Form.Select>
              {ticketErrors.category && (
                <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.category}</p>
              )}
            </Form.Group>
            {category !== '' && category !== 'Technical' && (
              <Form.Group className="mb-3"><Form.Label className="hs-label">Select Booking</Form.Label>
                <Form.Select className="hs-input" value={selectedBookingId} onChange={handleBookingSelect}>
                  <option value="">Select Booking</option>
                  {bookings.map((booking) => (<option key={booking.id} value={booking.id}>BK-{booking.id} - {booking.weevent ? booking.weevent.eventName : 'Event'} - {booking.date}</option>))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group className="mb-4">
              <Form.Label className="hs-label">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Explain your issue in detail..."
                className={`hs-input hs-textarea ${ticketErrors.description ? 'input-error' : ''}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setTicketErrors({ ...ticketErrors, description: regexChecker('description', e.target.value) });
                }}
              />
              {ticketErrors.description && (
                <p style={{ color: 'red', fontSize: '13px' }}>{ticketErrors.description}</p>
              )}
            </Form.Group>
            <div className="hs-modal-actions">
              <Button className="hs-btn-cancel" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button className="hs-btn-submit" onClick={handleSubmit}>Submit Ticket</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered className="hs-modal">
        <Modal.Body className="hs-modal-body">
          <button className="hs-modal-close" onClick={() => setShowEditModal(false)}>&#x2715;</button>
          <h5 className="hs-modal-title">Edit Support Ticket</h5>
          <p className="hs-modal-subtitle">Update your ticket details below.</p>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="hs-label">Subject</Form.Label>
              <Form.Control
                type="text"
                className={`hs-input ${editTicketErrors.subject ? 'input-error' : ''}`}
                value={editSubject}
                onChange={(e) => {
                  setEditSubject(e.target.value);
                  setEditTicketErrors({ ...editTicketErrors, subject: regexChecker('subject', e.target.value) });
                }}
              />
              {editTicketErrors.subject && (
                <p style={{ color: 'red', fontSize: '13px' }}>{editTicketErrors.subject}</p>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="hs-label">Category</Form.Label>
              <Form.Select
                className={`hs-input ${editTicketErrors.category ? 'input-error' : ''}`}
                value={editCategory}
                onChange={(e) => {
                  setEditCategory(e.target.value);
                  setEditTicketErrors({ ...editTicketErrors, category: e.target.value ? '' : 'Category is required' });
                }}
              >
                <option value="">Select</option>
                <option value="Booking">Booking</option>
                <option value="Payment">Payment</option>
                <option value="Service">Service</option>
                <option value="Technical">Technical</option>
                <option value="Other">Other</option>
              </Form.Select>
              {editTicketErrors.category && (
                <p style={{ color: 'red', fontSize: '13px' }}>{editTicketErrors.category}</p>
              )}
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label className="hs-label">Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                className={`hs-input hs-textarea ${editTicketErrors.description ? 'input-error' : ''}`}
                value={editDescription}
                onChange={(e) => {
                  setEditDescription(e.target.value);
                  setEditTicketErrors({ ...editTicketErrors, description: regexChecker('description', e.target.value) });
                }}
              />
              {editTicketErrors.description && (
                <p style={{ color: 'red', fontSize: '13px' }}>{editTicketErrors.description}</p>
              )}
            </Form.Group>
            <div className="hs-modal-actions">
              <Button className="hs-btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button className="hs-btn-submit" onClick={handleUpdate}>Update Ticket</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

    </CustomerLayout>
  );
};

export default HelpandSupport;